
export interface NextPromptItem {
  name: string;
  reason: string;
  addonFieldIds: string[];
  addonInstruction: string;
}

export type NextPromptsMap = Record<string, NextPromptItem[]>;

export const NEXT_PROMPTS: NextPromptsMap = {

  'Internal Communications Plan': [
    {
      name: 'Internal Announcement', reason: 'Draft the employee-facing announcement',
      addonFieldIds: ['ANNOUNCEMENT_TYPE'],
      addonInstruction: `Also draft the internal employee announcement for this initiative.

Announcement Type: [ANNOUNCEMENT_TYPE]

Include: a subject line that is direct and specific; an opening paragraph that leads with the key message and no preamble; a clear explanation of what is changing, why, and what it means for employees; concrete next steps with owners and timelines; and manager talking points for cascading and handling follow-up questions.`
    },
    {
      name: 'Q&A Preparation — Internal', reason: 'Prepare leadership for the live session',
      addonFieldIds: ['FORMAT', 'KEY_MESSAGE', 'KNOWN_CONCERNS'],
      addonInstruction: `Also produce an internal Q&A document to prepare the leader for the employee session on this topic.

Session Format: [FORMAT]
Key Message to Reinforce: [KEY_MESSAGE]
Known Employee Concerns: [KNOWN_CONCERNS]

Include: an opening statement that sets the tone and signals the leader is there to listen; the full range of questions employees are likely to raise — from procedural to hard and emotional; for the hardest questions, a bridge phrase, a full suggested answer, and what to avoid saying; a protocol for moments of silence or public confrontation; and a closing that confirms what happens next.`
    },
    {
      name: 'Executive Communications Brief', reason: 'Brief senior leadership before the cascade',
      addonFieldIds: ['AUDIENCE', 'USE_CASE', 'URGENCY', 'SENSITIVITY'],
      addonInstruction: `Also produce an executive briefing on this initiative for senior leadership.

Briefing For: [AUDIENCE]
Use Case: [USE_CASE]
Urgency: [URGENCY]
Sensitivity: [SENSITIVITY]

Write for someone who may read only the first paragraph. Include: a 3–4 sentence summary with the key point first; the 2–3 most critical things this leader needs to know; one "what to watch" — the risk or unknown that could change the picture; and a clear recommended action.`
    }
  ],
  'External Communications Plan': [
    {
      name: 'Media Response Draft', reason: 'Draft the external-facing materials',
      addonFieldIds: ['MEDIA_TYPE', 'RESPONSE_TYPE', 'KEY_MESSAGE'],
      addonInstruction: `Also draft the media communications materials for this initiative.

Media Type / Outlet: [MEDIA_TYPE]
Response Type: [RESPONSE_TYPE]
Key Message to Protect: [KEY_MESSAGE]

Include: headline and subheadline options with at least two angles; a strong lead paragraph; a spokesperson quote that sounds like a person, not a prepared statement; anticipated reporter follow-up questions with full responses including adversarial ones; and a clear note on what should not be confirmed or speculated on.`
    },
    {
      name: 'Social Media Content Draft', reason: 'Build out the digital content',
      addonFieldIds: ['PLATFORM', 'CONTENT_GOAL'],
      addonInstruction: `Also draft social media content to support this initiative.

Platform: [PLATFORM]
Content Goal: [CONTENT_GOAL]

Produce: a primary post optimized for the lead platform; at least two variations for testing or cross-platform use; platform-specific notes on length, hashtags, and posting context; suggested visual direction; and any approval flags for claims that need review.`
    },
    {
      name: 'Stakeholder Message Stress Test', reason: 'Test your messaging before it goes out',
      addonFieldIds: ['STRESS_TEST_AUDIENCES', 'KNOWN_VULNERABILITIES'],
      addonInstruction: `Also stress test the core narrative from this plan before it goes out.

Audiences to Test Against: [STRESS_TEST_AUDIENCES]
Known Vulnerabilities: [KNOWN_VULNERABILITIES]

For each audience assess: how they will likely receive this message; the top objections and the specific language that triggers each one; emotional vulnerabilities — what feels tone-deaf or inauthentic; media amplification risk; what is missing that this audience needs to hear; and specific recommended improvements.`
    }
  ],
  'Stakeholder Engagement Strategy': [
    {
      name: 'Meeting Prep Brief', reason: 'Prepare for the first engagement',
      addonFieldIds: ['MEETING_TYPE', 'DESIRED_OUTCOME', 'PRIOR_RELATIONSHIP'],
      addonInstruction: `Also prepare a meeting brief for the first engagement with this stakeholder.

Meeting Type: [MEETING_TYPE]
Desired Outcome: [DESIRED_OUTCOME]
Prior Relationship: [PRIOR_RELATIONSHIP]

Include: the single most important outcome this meeting must achieve; key discussion points with the strategic intent behind each; the explicit ask — what we want the stakeholder to commit to; potential objections and how to handle them; and what to avoid or handle with care.`
    },
    {
      name: 'Q&A Preparation — External / Media', reason: 'Ready your spokesperson for tough questions',
      addonFieldIds: ['SETTING', 'OFF_LIMITS'],
      addonInstruction: `Also prepare a Q&A document for external engagement on this stakeholder situation.

Engagement Setting: [SETTING]
Topics We Cannot Comment On: [OFF_LIMITS]

Include: a one-paragraph spokesperson briefing on who this audience is and what they are bringing into the room; questions from expected and low-risk to adversarial; for the hardest questions, bridge language and what not to say; and a protocol for sensitive topics.`
    }
  ],
  'Risk Assessment Summary': [
    {
      name: 'Crisis Communications Response', reason: 'If a risk materializes, be ready to respond',
      addonFieldIds: ['RESPONSE_STAGE', 'PRIMARY_AFFECTED_AUDIENCE', 'MEDIA_STATUS'],
      addonInstruction: `Also produce crisis communications materials in the event one of these risks materializes.

Response Stage: [RESPONSE_STAGE]
Primary Affected Audience: [PRIMARY_AFFECTED_AUDIENCE]
Current Media Status: [MEDIA_STATUS]

Include: a holding statement that can be used immediately — empathetic, factual, non-speculative; a clear summary of what is confirmed versus still unknown; audience-specific key messages; a spokesperson Q&A covering expected and legally sensitive questions; internal manager talking points; and escalation triggers.`
    },
    {
      name: 'Executive Communications Brief', reason: 'Bring leadership up to speed on key risks',
      addonFieldIds: ['AUDIENCE', 'URGENCY', 'SENSITIVITY'],
      addonInstruction: `Also produce an executive briefing that surfaces the most critical findings from this risk assessment.

Briefing For: [AUDIENCE]
Urgency: [URGENCY]
Sensitivity: [SENSITIVITY]

Write for someone who may read only the first paragraph. Include: a 3–4 sentence summary of the top risks and their implications; the 2–3 most critical things this leader needs to know and act on; the single biggest "what to watch"; and a clear recommended action or decision needed.`
    },
    {
      name: 'Stakeholder Message Stress Test', reason: 'Test your messaging against each risk area',
      addonFieldIds: ['STRESS_TEST_AUDIENCES', 'KNOWN_VULNERABILITIES'],
      addonInstruction: `Also stress test the company's current messaging against the risks identified in this assessment.

Audiences to Test Against: [STRESS_TEST_AUDIENCES]
Known Vulnerabilities: [KNOWN_VULNERABILITIES]

For each audience, assess how the current messaging holds up against these risks — what objections they will raise, what emotional vulnerabilities exist, how a journalist or activist could weaponize the gaps, and what specific improvements would strengthen the position.`
    }
  ],
  'Internal Announcement': [
    {
      name: 'Q&A Preparation — Internal', reason: 'Prepare managers for follow-up questions',
      addonFieldIds: ['FORMAT', 'KEY_MESSAGE', 'KNOWN_CONCERNS'],
      addonInstruction: `Also produce a Q&A document to prepare the leader or managers for employee questions following this announcement.

Session Format: [FORMAT]
Key Message to Reinforce: [KEY_MESSAGE]
Known Employee Concerns: [KNOWN_CONCERNS]

Include: an opening statement that sets the tone; the full range of questions employees are likely to ask — from procedural to hard and emotional; for the hardest questions, a bridge phrase, full suggested answer, and what to avoid saying; a protocol for moments of silence or confrontation; and a strong close.`
    },
    {
      name: 'Speaking Notes / Talking Points', reason: 'Equip your leader for the live session',
      addonFieldIds: ['EVENT_SETTING', 'FORMAT', 'DURATION', 'PRIORITY_MESSAGE'],
      addonInstruction: `Also produce speaking notes for the leader to use when delivering this announcement live.

Event / Setting: [EVENT_SETTING]
Format: [FORMAT]
Duration: [DURATION]
Priority Message: [PRIORITY_MESSAGE]

Include: an opening hook that earns the room's trust in the first 30 seconds; a narrative arc that builds to the priority message; 3–4 talking points each grounded in a specific example or fact; anticipated Q&A with full responses including the hardest questions; and a closing that gives employees one clear thing to hold onto.`
    }
  ],
  'Media Response Draft': [
    {
      name: 'Q&A Preparation — External / Media', reason: 'Prepare your spokesperson for follow-up',
      addonFieldIds: ['EXTERNAL_AUDIENCE', 'SETTING', 'OFF_LIMITS'],
      addonInstruction: `Also prepare a media Q&A to get the spokesperson ready for follow-up questions on this story.

Audience Type: [EXTERNAL_AUDIENCE]
Engagement Setting: [SETTING]
Topics We Cannot Comment On: [OFF_LIMITS]

Include: a one-paragraph spokesperson briefing on what this audience already knows and is watching for; questions from expected to adversarial — written the way a well-researched skeptic would actually ask them; for the hardest questions, bridge language and what not to say; and a protocol for on the record versus background.`
    },
    {
      name: 'Social Media Content Draft', reason: 'Extend the story across digital channels',
      addonFieldIds: ['PLATFORM', 'CONTENT_GOAL'],
      addonInstruction: `Also draft social media content to extend this story across digital channels.

Platform: [PLATFORM]
Content Goal: [CONTENT_GOAL]

Produce: a primary post that leads with what the audience finds interesting; at least two variations for testing or cross-platform use; platform-specific notes on length and hashtags; suggested visual direction; and any compliance flags for claims that need review before going live.`
    }
  ],
  'Corporate Storytelling Draft': [
    {
      name: 'Social Media Content Draft', reason: 'Adapt the story for social platforms',
      addonFieldIds: ['PLATFORM', 'CONTENT_GOAL'],
      addonInstruction: `Also adapt this story for social media.

Platform: [PLATFORM]
Content Goal: [CONTENT_GOAL]

Produce: a primary post that distills the story to its most shareable moment — not a summary, but a hook that pulls the reader in; at least two variations for testing different angles; platform-specific notes on length and hashtags; suggested visual direction that complements the narrative; and any compliance flags.`
    },
    {
      name: 'Media Response Draft', reason: 'Build out the media-facing version',
      addonFieldIds: ['MEDIA_TYPE', 'RESPONSE_TYPE'],
      addonInstruction: `Also produce a media-facing version of this story.

Media Type / Outlet: [MEDIA_TYPE]
Response Type: [RESPONSE_TYPE]

Include: headline and subheadline options with at least two angles; a strong news lead; a spokesperson quote that sounds like a real person; supporting body with relevant data and context; and a note on what should not be confirmed or speculated on.`
    }
  ],
  'Social Media Content Draft': [
    {
      name: 'Corporate Storytelling Draft', reason: 'Develop the full narrative behind this post',
      addonFieldIds: ['STORY_SUBJECT', 'STORY_ANGLE', 'PUBLICATION_CHANNEL'],
      addonInstruction: `Also develop the full narrative story that this social content is drawn from.

Story Subject: [STORY_SUBJECT]
Story Angle: [STORY_ANGLE]
Publication Channel: [PUBLICATION_CHANNEL]

Open with a specific scene, person, or moment — never a company statement. Build through concrete details. Every paragraph must earn its place. End on the message, not the company. Include headline options, a pull quote for callout formatting, and a brief summary line for metadata.`
    },
    {
      name: 'Media Response Draft', reason: 'Build a press release to support the story',
      addonFieldIds: ['MEDIA_TYPE', 'RESPONSE_TYPE'],
      addonInstruction: `Also draft the media materials to formally support this story.

Media Type / Outlet: [MEDIA_TYPE]
Response Type: [RESPONSE_TYPE]

Include headline and subheadline options, a strong news lead, a spokesperson quote that is specific and ready to use, supporting body with context and data, and a clear note on what should not be confirmed or speculated on.`
    }
  ],
  'Crisis Communications Response': [
    {
      name: 'Media Response Draft', reason: 'Draft the formal media statement',
      addonFieldIds: ['MEDIA_TYPE', 'KEY_MESSAGE'],
      addonInstruction: `Also draft the formal media statement for this situation.

Media Type / Outlet: [MEDIA_TYPE]
Key Message to Protect: [KEY_MESSAGE]

Lead with the most newsworthy confirmed facts. Include: a headline; a news lead; a spokesperson quote that is empathetic and specific; supporting context; anticipated reporter follow-up questions with full responses including the adversarial ones; and a clear note on what cannot be confirmed or speculated on at this stage.`
    },
    {
      name: 'Internal Announcement', reason: 'Communicate directly to employees',
      addonFieldIds: ['ANNOUNCEMENT_TYPE'],
      addonInstruction: `Also draft the internal employee announcement for this situation.

Announcement Type: [ANNOUNCEMENT_TYPE]

Lead with what employees need to know right now. Be direct about what is confirmed and what is still developing — employees will hear about this elsewhere if they haven't already. Include concrete next steps, manager talking points, and a clear signal of what happens next and who is accountable.`
    },
    {
      name: 'Q&A Preparation — External / Media', reason: 'Prepare your spokesperson',
      addonFieldIds: ['EXTERNAL_AUDIENCE', 'SETTING', 'OFF_LIMITS'],
      addonInstruction: `Also prepare the spokesperson Q&A for external engagement on this situation.

Audience Type: [EXTERNAL_AUDIENCE]
Engagement Setting: [SETTING]
Topics We Cannot Comment On: [OFF_LIMITS]

Include: a one-paragraph briefing on what this audience already knows and is watching for; questions from expected to adversarial — including the ones the company hopes don't come up; for the hardest questions, bridge language and what not to say; and a protocol for on the record versus background.`
    }
  ],
  'Executive Communications Brief': [
    {
      name: 'Speaking Notes / Talking Points', reason: 'Build out full remarks from the brief',
      addonFieldIds: ['EVENT_SETTING', 'FORMAT', 'DURATION', 'PRIORITY_MESSAGE'],
      addonInstruction: `Also develop speaking notes and talking points based on this briefing.

Event / Setting: [EVENT_SETTING]
Format: [FORMAT]
Duration: [DURATION]
Priority Message: [PRIORITY_MESSAGE]

Include: an opening hook that earns the room's attention in the first 30 seconds; a narrative arc that builds toward the priority message; 3–5 talking points each anchored in a specific example or data point; anticipated Q&A including the hardest questions; and a closing that gives the audience one clear thing to hold onto.`
    },
    {
      name: 'Meeting Prep Brief', reason: 'Prepare for the leadership conversation',
      addonFieldIds: ['MEETING_COUNTERPART', 'MEETING_TYPE', 'DESIRED_OUTCOME', 'PRIOR_RELATIONSHIP'],
      addonInstruction: `Also produce a meeting prep brief using the context from this executive briefing.

Meeting Counterpart: [MEETING_COUNTERPART]
Meeting Type: [MEETING_TYPE]
Desired Outcome: [DESIRED_OUTCOME]
Prior Relationship: [PRIOR_RELATIONSHIP]

Include: the single most important outcome this meeting must achieve; a counterpart profile covering what they care about and their history with Chevron; key discussion points with strategic intent; the explicit ask; potential objections and how to handle them; and what to avoid.`
    }
  ],
  'Speaking Notes / Talking Points': [
    {
      name: 'Q&A Preparation — External / Media', reason: 'Prepare for questions after the remarks',
      addonFieldIds: ['EXTERNAL_AUDIENCE', 'SETTING', 'OFF_LIMITS'],
      addonInstruction: `Also prepare the speaker for questions following these remarks.

Audience Type: [EXTERNAL_AUDIENCE]
Engagement Setting: [SETTING]
Topics We Cannot Comment On: [OFF_LIMITS]

Write questions as a well-researched skeptic would actually ask them — including the ones the speaker hopes don't come up. For the hardest questions: bridge language, a full suggested response, and what not to say. Include a protocol for sensitive topics.`
    },
    {
      name: 'Q&A Preparation — Internal', reason: 'Prepare for employee questions',
      addonFieldIds: ['FORMAT', 'KEY_MESSAGE', 'KNOWN_CONCERNS'],
      addonInstruction: `Also prepare the speaker for employee questions following these remarks.

Session Format: [FORMAT]
Key Message to Reinforce: [KEY_MESSAGE]
Known Employee Concerns: [KNOWN_CONCERNS]

Write questions exactly as an employee would actually ask them — including the uncomfortable and skeptical ones. Include bridge phrases for the hardest questions, full suggested answers, what to avoid saying, and a protocol for moments of emotion or public confrontation.`
    }
  ],
  'Meeting Prep Brief': [
    {
      name: 'Speaking Notes / Talking Points', reason: 'Build opening remarks for the meeting',
      addonFieldIds: ['FORMAT', 'DURATION', 'PRIORITY_MESSAGE'],
      addonInstruction: `Also prepare opening remarks for the executive to use in this meeting.

Format: [FORMAT]
Duration: [DURATION]
Priority Message: [PRIORITY_MESSAGE]

Include: an opening that establishes purpose and sets the right tone; 3–4 key points each grounded in a specific example or fact; the explicit ask framed for maximum receptivity; and a close that advances the desired outcome.`
    },
    {
      name: 'Q&A Preparation — External / Media', reason: 'Prepare for difficult questions',
      addonFieldIds: ['SETTING', 'OFF_LIMITS'],
      addonInstruction: `Also prepare the executive for difficult questions in this meeting.

Setting: [SETTING]
Topics We Cannot Comment On: [OFF_LIMITS]

Write questions as the counterpart would actually ask them — including those designed to put the company on the defensive. For the hardest questions: bridge language, a full suggested response, and what not to say. Include a protocol for anything that cannot be addressed directly in this meeting.`
    }
  ],
  'Q&A Preparation — Internal': [
    {
      name: 'Speaking Notes / Talking Points', reason: 'Build full remarks around these Q&As',
      addonFieldIds: ['EVENT_SETTING', 'FORMAT', 'DURATION', 'PRIORITY_MESSAGE'],
      addonInstruction: `Also develop full speaking notes for the leader to use in this session, built around the Q&A above.

Event / Setting: [EVENT_SETTING]
Format: [FORMAT]
Duration: [DURATION]
Priority Message: [PRIORITY_MESSAGE]

Include: an opening hook that earns the room's trust in the first 30 seconds; a clear narrative arc that builds to the priority message; 3–4 key points each grounded in a specific example; and a closing that gives employees one clear thing to hold onto and confirms what happens next.`
    },
    {
      name: 'Internal Announcement', reason: 'Draft the formal announcement to accompany this session',
      addonFieldIds: ['ANNOUNCEMENT_TYPE'],
      addonInstruction: `Also draft the formal internal announcement that will accompany or precede this session.

Announcement Type: [ANNOUNCEMENT_TYPE]

Lead with the key message — no preamble. Be as direct in writing as the leader will be in person. Include: a subject line that tells employees exactly what this is; a clear explanation of what is changing and why; concrete next steps with owners and timelines; and manager talking points aligned with the session.`
    }
  ],
  'Q&A Preparation — External / Media': [
    {
      name: 'Media Response Draft', reason: 'Build out the formal media materials',
      addonFieldIds: ['MEDIA_TYPE', 'RESPONSE_TYPE', 'KEY_MESSAGE'],
      addonInstruction: `Also draft the formal media materials to accompany this spokesperson preparation.

Media Type / Outlet: [MEDIA_TYPE]
Response Type: [RESPONSE_TYPE]
Key Message to Protect: [KEY_MESSAGE]

Include: headline and subheadline options; a strong news lead; a spokesperson quote that sounds like a real person; supporting body with context and data; and a clear note on what should not be confirmed — aligned with the Q&A restrictions above.`
    },
    {
      name: 'Crisis Communications Response', reason: 'If this is escalating, prepare crisis materials',
      addonFieldIds: ['RESPONSE_STAGE', 'PRIMARY_AFFECTED_AUDIENCE', 'MEDIA_STATUS'],
      addonInstruction: `Also develop crisis communications materials in case this situation escalates.

Response Stage: [RESPONSE_STAGE]
Primary Affected Audience: [PRIMARY_AFFECTED_AUDIENCE]
Current Media Status: [MEDIA_STATUS]

Include: a holding statement that can be used immediately; a clear summary of what is confirmed versus still unknown; audience-specific key messages; escalation triggers that would require a material change in response posture; and internal manager talking points.`
    }
  ],
  'Policy Position Summary': [
    {
      name: 'Stakeholder Engagement Strategy', reason: 'Build the engagement plan around this position',
      addonFieldIds: ['STAKEHOLDER_GROUP', 'RELATIONSHIP_STATUS', 'ENGAGEMENT_OBJECTIVE'],
      addonInstruction: `Also develop a stakeholder engagement strategy to advance this policy position.

Stakeholder Group: [STAKEHOLDER_GROUP]
Current Relationship Status: [RELATIONSHIP_STATUS]
Engagement Objective: [ENGAGEMENT_OBJECTIVE]

Include: a stakeholder profile covering who they are and what they care about; key messages tailored to this audience's actual interests; recommended engagement activities and timeline; anticipated objections and how to handle them; and a path forward if the engagement stalls or turns adversarial.`
    },
    {
      name: 'Meeting Prep Brief', reason: 'Prepare for the legislative or regulatory meeting',
      addonFieldIds: ['MEETING_COUNTERPART', 'MEETING_TYPE', 'DESIRED_OUTCOME', 'PRIOR_RELATIONSHIP'],
      addonInstruction: `Also prepare a meeting brief for the first legislative or regulatory meeting on this policy issue.

Meeting Counterpart: [MEETING_COUNTERPART]
Meeting Type: [MEETING_TYPE]
Desired Outcome: [DESIRED_OUTCOME]
Prior Relationship: [PRIOR_RELATIONSHIP]

Include: the single most important outcome this meeting must achieve; a counterpart profile with their known positions on this issue; key discussion points with strategic intent; the explicit ask; the questions we hope don't come up and how to handle them; and what to avoid.`
    },
    {
      name: 'Stakeholder Message Stress Test', reason: 'Test your position against key audiences',
      addonFieldIds: ['STRESS_TEST_AUDIENCES', 'KNOWN_VULNERABILITIES'],
      addonInstruction: `Also stress test this policy position against key audiences before it goes out.

Audiences to Test Against: [STRESS_TEST_AUDIENCES]
Known Vulnerabilities: [KNOWN_VULNERABILITIES]

For each audience assess: how they will receive this position; the top objections and the specific language that triggers each; emotional vulnerabilities; how a critic or adversarial journalist could reframe it; what this audience needs to hear that is currently missing; and specific recommended improvements.`
    }
  ],
  'Stakeholder Message Stress Test': [
    {
      name: 'External Communications Plan', reason: 'Rebuild your plan based on stress test findings',
      addonFieldIds: ['PRIMARY_AUDIENCE', 'GEOGRAPHIC_SCOPE', 'TIMELINE', 'PRIMARY_OBJECTIVE'],
      addonInstruction: `Based on the stress test findings above, also build a revised external communications plan that addresses the vulnerabilities identified.

Primary External Audience: [PRIMARY_AUDIENCE]
Geographic Scope: [GEOGRAPHIC_SCOPE]
Campaign Timeline: [TIMELINE]
Primary Objective: [PRIMARY_OBJECTIVE]

The plan should directly respond to the objections, gaps, and amplification risks surfaced in the stress test. Include a revised message platform, channel mix and sequencing, how the plan proactively addresses the top objections, and key risks.`
    },
    {
      name: 'Media Response Draft', reason: 'Strengthen your media materials',
      addonFieldIds: ['MEDIA_TYPE', 'RESPONSE_TYPE', 'KEY_MESSAGE'],
      addonInstruction: `Based on the stress test findings above, also draft strengthened media materials that close the vulnerabilities identified.

Media Type / Outlet: [MEDIA_TYPE]
Response Type: [RESPONSE_TYPE]
Key Message (revised): [KEY_MESSAGE]

Apply the stress test findings directly — close the gaps, sharpen the language, and anticipate the adversarial angles identified. Include revised headline options, a stronger lead, an improved spokesperson quote, and full responses to the hardest reporter questions surfaced in the stress test.`
    }
  ]
};
